import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrowthShell from '../../components/growth/GrowthShell';
import RangeBar from '../../components/growth/GrowthRange';
import ClaritySessions from '../../components/growth/ClaritySessions';
import { CHANNEL_AR, FIELD_AR, METHOD_AR, REASON_AR, SERVICE_AR, fetchJourney } from '../../lib/growthJourney';
import { MIN_SAMPLE, rangeLabel, rangeWindow, readRange, spanLabel, writeRange } from '../../lib/growthRange';

// Growth OS «رحلة العميل» — the sign-up funnel (with failure reasons per code
// channel) and a funnel per service. Counted per browser from clean events;
// a step rate below MIN_SAMPLE is greyed «بيانات قليلة».

const LOW = 'بيانات قليلة';
const MODE_AR = { signup: 'تسجيل', login: 'دخول', checkout: 'صفحة الدفع' };
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

function StepRate({ n, prev, label = 'من السابقة' }) {
  if (prev == null || prev === 0) return null;
  const pct = Math.round((n / prev) * 1000) / 10;
  const low = prev < MIN_SAMPLE;
  return (
    <span className={`gos-steprate${low ? ' low' : ''}`} title={low ? `${LOW} (${fmt(prev)}؛ الحد ${MIN_SAMPLE})` : undefined} data-low-sample={low ? '1' : '0'}>
      <bdi dir="ltr">{pct}%</bdi> {label}{low && <small> · {LOW}</small>}
    </span>
  );
}

function Funnel({ steps, note }) {
  const top = steps[0]?.n || 0;
  if (!top) return <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong>تظهر الخطوات حين تصل أولى الزيارات في هذه الفترة.</div>;
  return (
    <>
      <div className="gos-funnel">
        {steps.map((s) => (
          <React.Fragment key={s.key}>
            <div className="gos-fstep wide" data-funnel-step={s.key}>
              <span className="gos-fstep-label">{s.label}</span>
              <div className="gos-fbar">{s.n > 0 && <span style={{ width: `${Math.min(100, (s.n / top) * 100)}%` }} />}</div>
              <span className="gos-fnum">{fmt(s.n)}</span>
              <StepRate n={s.n} prev={s.prev} label={s.prevLabel} />
            </div>
            {s.drops?.length > 0 && <ClaritySessions sessions={s.drops} label="توقفوا هنا" />}
          </React.Fragment>
        ))}
      </div>
      {note && <p className="gos-note">{note}</p>}
    </>
  );
}

export default function GrowthJourney({ user, profile, lang }) {
  const [params, setParams] = useSearchParams();
  const range = useMemo(() => readRange(params), [params]);
  const win = useMemo(() => rangeWindow(range), [range]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null); setErr(false);
    fetchJourney(win).then((d) => { if (alive) setData(d); })
      .catch((e) => { console.warn('[GrowthJourney] load failed:', e?.message || e); if (alive) setErr(true); });
    return () => { alive = false; };
  }, [win.start, win.end]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="journey" title="رحلة العميل"
                 actions={<span className="gos-top-range">{spanLabel(range.from, range.to)}</span>}>
      <div className="gos-hello">
        <h2>رحلة العميل</h2>
        <p>{rangeLabel(range)}. كل خطوة تعدّ المتصفحات التي وصلت إليها، فمن أعاد طلب الرمز ثلاث مرات يُحسب مرة واحدة.</p>
      </div>
      <RangeBar range={range} onChange={(next) => setParams(writeRange(next))} compare={false} />
      {err && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state"><strong>تعذّر تحميل البيانات</strong>أعد تحميل الصفحة.</div></div>}
      {data?.capped && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state">هذه الفترة تجاوزت حدّ الصفوف؛ الأرقام ناقصة. اختر فترة أقصر.</div></div>}

      <div className="gos-row r-journey">
        <section className="gos-card" data-section="signup">
          <div className="gos-card-head"><h3>قمع التسجيل</h3></div>
          <p className="gos-card-sub">من فتح صفحة التسجيل إلى إنشاء الحساب.</p>
          {!data ? <div className="gos-empty-state">…</div> : (
            <Funnel steps={data.steps} note="Google يتخطى خطوتي الرمز، لذلك تُقاس «أُرسل الرمز» ممن اختار طريقة برمز، و«أُنشئ الحساب» ممن اختار أي طريقة. التفصيل لكل طريقة في الجدول." />
          )}
          {data && data.methods.length > 0 && (
            <table className="gos-table" style={{ marginTop: 14 }} data-methods="1">
              <thead><tr><th>الطريقة</th><th>اختارها</th><th>أنشأ حساباً</th><th>النسبة</th></tr></thead>
              <tbody>{data.methods.map((m) => {
                const low = m.chose < MIN_SAMPLE;
                return (
                  <tr key={m.method}><td>{METHOD_AR[m.method] || m.method}</td><td className="num">{fmt(m.chose)}</td><td className="num">{fmt(m.created)}</td>
                    <td className={`num${low ? ' low' : ''}`} title={low ? LOW : undefined}>{m.chose ? `${Math.round((m.created / m.chose) * 1000) / 10}%` : '—'}</td></tr>
                );
              })}</tbody>
            </table>
          )}
        </section>

        <section className="gos-card" data-section="failures">
          <div className="gos-card-head"><h3>أسباب فشل الرمز</h3></div>
          <p className="gos-card-sub">لكل قناة: كم مرة فشل الإرسال أو التحقق، وأكثر الأسباب. لا يُحفظ الرمز ولا الرقم ولا البريد.</p>
          {!data ? <div className="gos-empty-state">…</div> : data.failures.length === 0 ? (
            <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong>لم يفشل أي رمز في هذه الفترة.</div>
          ) : (
            <div className="gos-fails">
              {data.failures.map((f) => (
                <div key={`${f.channel}|${f.mode}`} className="gos-fail" data-channel={f.channel} data-mode={f.mode}>
                  <div className="gos-fail-head"><b>{CHANNEL_AR[f.channel] || f.channel}</b><span>{MODE_AR[f.mode] || f.mode}</span>
                    <span className="gos-faint">{fmt(f.total)} فشل · {fmt(f.sent)} إرسال</span></div>
                  <ul>{f.top.map(([r, n]) => <li key={r}><span>{REASON_AR[r] || r}</span><b>{fmt(n)}</b></li>)}</ul>
                </div>
              ))}
            </div>
          )}
          <p className="gos-note">على الموقع تصل رموز الجوال عبر واتساب، ورموز البريد بالبريد؛ لا تُرسل رسائل نصية.</p>
        </section>
      </div>

      <section className="gos-card" data-section="services" style={{ marginBottom: 16 }}>
        <div className="gos-card-head"><h3>الخدمات</h3></div>
        <p className="gos-card-sub">لكل خدمة: من بدأ النموذج، ومن رأى الرسوم، ومن أرسل، ومن دفع. «دفع الرسوم» يسجّله الخادم عند تأكيد الدفع.</p>
        {!data ? <div className="gos-empty-state">…</div> : (
          <div className="gos-services">
            {data.services.map((s) => (
              <div key={s.service} className="gos-service" data-service={s.service}>
                <h4>{SERVICE_AR[s.service]}</h4>
                <Funnel steps={s.steps} />
                {s.steps[0].n > 0 && (
                  <p className="gos-note">
                    تركوا النموذج: <b>{fmt(s.abandoned)}</b>
                    {s.errors.length > 0 && <> · أكثر الحقول الناقصة: {s.errors.map(([f, n]) => `${FIELD_AR[f] || f} (${fmt(n)})`).join('، ')}</>}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </GrowthShell>
  );
}
